import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  ListChecks,
  Users,
  UserCog,
  LogOut,
  ShieldCheck,
  ClipboardCheck,
  History,
  CreditCard,
  Settings as SettingsIcon,
} from "lucide-react";
import WorkerAvatar from "@/components/WorkerAvatar";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [org, setOrg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    api.get("/orgs/current").then(({ data }) => {
      if (!cancelled) setOrg(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const brand = {
    name: org?.name || "Audentra",
    tagline: org?.name && org.id !== "cura-default" ? "Compliance Hub" : "Compliance Hub",
    color: org?.brand_primary_color || "#012E88",
    logo: org?.brand_logo_url || null,
  };

  const nav = [];
  if (user?.role === "worker") {
    nav.push({ to: "/dashboard", label: "My Checklist", icon: ListChecks, testid: "nav-my-checklist" });
  }
  if (user?.role === "team_member" || user?.role === "manager") {
    nav.push({ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" });
    nav.push({ to: "/workers", label: "Workers", icon: Users, testid: "nav-workers" });
    nav.push({ to: "/review", label: "Review queue", icon: ClipboardCheck, testid: "nav-review" });
  }
  if (user?.role === "manager") {
    nav.push({ to: "/team", label: "Team", icon: UserCog, testid: "nav-team" });
    nav.push({ to: "/audit-log", label: "Audit log", icon: History, testid: "nav-audit-log" });
    nav.push({ to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" });
    nav.push({ to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex lg:flex-col w-72 min-h-screen bg-slate-900 text-slate-100 sticky top-0">
          <div className="px-8 pt-10 pb-8 border-b border-slate-800">
            {brand.logo ? (
              <div className="rounded-lg bg-white p-3 flex items-center justify-center" data-testid="brand-logo-wrap">
                <img
                  src={brand.logo}
                  alt={brand.name}
                  data-testid="brand-logo"
                  className="h-10 w-auto max-w-full object-contain"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <div
                  className="h-9 w-9 rounded-md flex items-center justify-center"
                  style={{ backgroundColor: brand.color }}
                  data-testid="brand-avatar"
                >
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold tracking-tight" data-testid="brand-name">{brand.name}</div>
                  <div className="text-[0.65rem] tracking-[0.2em] uppercase text-slate-400">{brand.tagline}</div>
                </div>
              </div>
            )}
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {nav.map((item) => {
              const Active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    Active
                      ? "bg-slate-800 text-white"
                      : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="px-4 pb-6 pt-2 border-t border-slate-800">
            <div className="px-4 pt-4 pb-3">
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-slate-400 mb-1">Signed in as</div>
              <div className="flex items-center gap-2.5">
                <WorkerAvatar workerId={user?.id} name={user?.name} size="sm" />
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium truncate" data-testid="sidebar-user-name">
                    {user?.name}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{user?.email}</div>
                </div>
              </div>
              <div className="mt-2 inline-flex rounded-full bg-slate-800 text-slate-200 px-2 py-0.5 text-[0.65rem] uppercase tracking-wider">
                {user?.role?.replace("_", " ")}
              </div>
            </div>
            <Button
              variant="ghost"
              data-testid="logout-button"
              onClick={handleLogout}
              className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0">
          {/* Mobile header */}
          <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3">
            {brand.logo ? (
              <img src={brand.logo} alt={brand.name} className="h-8 w-auto max-w-[160px] object-contain" />
            ) : (
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-md flex items-center justify-center" style={{ backgroundColor: brand.color }}>
                  <ShieldCheck className="h-4 w-4 text-white" />
                </div>
                <div className="font-heading font-bold">{brand.name}</div>
              </div>
            )}
            <Button variant="ghost" size="sm" data-testid="mobile-logout" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </header>

          {/* Mobile nav pills */}
          <div className="lg:hidden flex gap-2 overflow-x-auto px-4 pt-3 pb-1 bg-white border-b border-slate-100">
            {nav.map((item) => {
              const Active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`m-${item.testid}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap border ${
                    Active
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          <main className="p-6 md:p-8 lg:p-12 anim-fade-up">{children}</main>
        </div>
      </div>
    </div>
  );
}
